import { useCallback } from 'react';

import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	useGetCurrentBreakpoint,
	useGetValueByCreditType,
	useGtm,
	useHideScroll,
	useIsVisibleModal,
	useModal,
	useRedirectWithContext,
} from '@hooks';
import { useServicesStore, useSessionInfo } from '@store';
import { ViewSizeType } from '@types';

export interface AutoSimulationData {
	loanAmount: number;
	interestRate: number;
	termMonths: number;
	monthlyPayment?: number;
}

export interface UseAutoSimulationOutput {
	currentView: ViewSizeType;
	qrCodeToApp?: string | null;
	isVisible: boolean;
	handleOnClickClose: () => void;
	handleClickContinueApp: () => void;
	handleClickContinueToSite: () => void;
	handleOnClickRequestContact: () => void;
	translate: (key: string) => string;
}

export const useAutoSimulation = (): UseAutoSimulationOutput => {
	const translate = useTranslation();
	const { hide } = useModal('autoSimulate');
	const { currentView } = useGetCurrentBreakpoint();
	const sessionInfo = useSessionInfo();
	const { enable } = useHideScroll();
	const { qrCodeToApp, linkToApp } = useServicesStore();
	const isVisible = useIsVisibleModal('autoSimulate');
	const { navigate } = useRedirectWithContext();
	const creditType = sessionInfo?.journeyData.loanType || 'AL';
	const { sendEvent } = useGtm();

	// cSpell: disable
	const itemType = useGetValueByCreditType(
		{
			CL: 'credito-pessoal',
			AL: 'credito-auto',
			RENEN: 'credito-edificios-sustentaveis',
			ALEV: 'credito-eletricauto',
			HOME: 'credito-lar',
			AN: 'credito-auto-novos',
			CONSTR: 'credito-obras',
			EDUCA: 'credito-educacao',
			HEALTH: 'credito-saude',
		},
		'credito-pessoal'
	);
	// cSpell: enable

	const handleOnClickClose = useCallback(() => {
		sendEvent({
			// cSpell:disable
			eName: 'fechar',
			screen_name: `${itemType}/simulador-publico/modal-confirmar-avancar`,
			// cSpell:enable
		});
		enable();
		hide();
	}, [sendEvent, itemType, enable, hide]);

	const handleClickContinueApp = useCallback(() => {
		sendEvent({
			// cSpell:disable
			eName: 'continuar-app',
			screen_name: `${itemType}/simulador-publico/modal-confirmar-avancar`,
			// cSpell:enable
		});
		enable();
		hide();
		window.open(linkToApp as string, '_self');
	}, [enable, hide, itemType, linkToApp, sendEvent]);

	// eslint-disable-next-line sonarjs/no-identical-functions
	const handleClickContinueToSite = useCallback(() => {
		sendEvent(
			{
				// cSpell:disable
				eName: 'continuar-site',
				screen_name: `${itemType}/simulador-publico/modal-confirmar-avancar`,
				// cSpell:enable
			},
			true
		);
		enable();
		hide();
		// navigate({
		// 	amount: sessionInfo?.journeyData.amount || 0,
		// 	term: sessionInfo?.journeyData.term || 0,
		// });
		const url = translate(`simulator.dataDisplay.nextButton.link.${creditType}`);
		window.open(url, '_self');
	}, [creditType, enable, hide, itemType, sendEvent, translate]);

	const handleOnClickRequestContact = useCallback(() => {
		sendEvent(
			{
				// cSpell:disable
				eName: 'nao-sou-cliente-pedir-contacto',
				screen_name: `${itemType}/simulador-publico/modal-confirmar-avancar`,
				// cSpell:enable
			},
			true
		);
		enable();
		hide();
		const url = translate('simulator.modals.userIsClient.url.generalContacts');
		window.open(url, '_self');
	}, [enable, hide, itemType, sendEvent, translate]);

	return {
		currentView,
		qrCodeToApp,
		isVisible,
		handleOnClickClose,
		handleClickContinueApp,
		handleClickContinueToSite,
		handleOnClickRequestContact,
		translate,
	};
};
